import {
  canMergeFromCard,
  checksBlockMerge,
  mergeBlockedText,
  prMergeView,
  subtasksNotReadyText,
  type GithubPrLive,
  type PrChecksSummary,
  type PrMigrationsBlock,
  type SharedPrSubtask,
  type TaskPrsLiveResponse,
} from '@shared/github-pr';
import { sharedPrParent, type TaskColumn, type TaskParent, type TaskPr, type TaskWaiting } from '@shared/types/tasks';
import { prLabel } from './task-types';
import { tickColumn } from './subtasks';

/**
 * SF-T-79: the task panel's actions (Merge, Fix conflicts, Send to Review, Move to Done), worked out from
 * the task, its PRs as GitHub sees them, and what you may do. Kept out of the component for the tests.
 */

/** Live states only for pull requests that can still change; merged and closed ones stay as stored. */
export const isOpenPr = (pr: TaskPr) => pr.status === 'open' || pr.status === 'draft';

/**
 * Whether Merge is on for a live pull request, and why not (SF-T-79): GitHub must say it's ready, and
 * its checks (Local checks included) must have passed. `shown` is false where the state badge already
 * says why (conflicts, a draft…); a ready PR whose checks hold it shows Merge off, with the reason.
 * SF-T-154: so does a PR sub-tasks share while any of them isn't ready (`notReady`, on their parent).
 * SF-T-183: and a PR adding migrations while main has a newer one, or two migration heads (`migrations`).
 */
export function prMergeState(
  live: GithubPrLive | null,
  checks: PrChecksSummary | null | undefined,
  notReady?: SharedPrSubtask[] | null,
  migrations?: PrMigrationsBlock | null
): { shown: boolean; enabled: boolean; reason: string | null } {
  if (!live) return { shown: false, enabled: false, reason: null };
  const view = prMergeView(live);
  if (!canMergeFromCard(view)) return { shown: false, enabled: false, reason: mergeBlockedText(view) };
  if (notReady?.length) return { shown: true, enabled: false, reason: subtasksNotReadyText(notReady) };
  if (migrations) return { shown: true, enabled: false, reason: mergeBlockedText(migrations.reason, null, migrations.folders) };
  const blocked = checksBlockMerge(checks);
  return blocked ? { shown: true, enabled: false, reason: mergeBlockedText(blocked, checks) } : { shown: true, enabled: true, reason: null };
}

export type TaskActionKind = 'merge' | 'fix_conflicts' | 'update' | 'review' | 'done';

/**
 * SF-T-183: Update for a live PR: shown when it adds migrations and main has a newer one, for someone who
 * may start runs; off while the AI builder already has the task.
 */
export function prUpdateState(
  migrations: PrMigrationsBlock | null | undefined,
  may: { run: boolean },
  aiHasIt: boolean
): { shown: boolean; enabled: boolean; reason: string | null } {
  if (!may.run || migrations?.reason !== 'migrations_behind') return { shown: false, enabled: false, reason: null };
  return aiHasIt ? { shown: true, enabled: false, reason: 'The AI builder already has this task.' } : { shown: true, enabled: true, reason: null };
}

/** SF-T-183: what Update does, for its hint. */
export const updateHint = (label: string, baseRef: string) => `The AI builder merges ${baseRef || 'the base branch'} into ${label} and runs pnpm db:rebase, so its migrations come after ${baseRef || 'the base'}'s`;

export interface TaskActionPlan {
  key: string;
  kind: TaskActionKind;
  label: string;
  /** What it does, for the hint. */
  hint: string;
  enabled: boolean;
  /** Why it's off. */
  reason: string | null;
  /** The PR it's for (merge, fix_conflicts, update), or the column it moves to (review, done). */
  prUrl?: string;
  columnId?: string;
}

/** The Column where finished work waits to be checked: by name, as the worker finds it (services/tasks/move.ts). */
export function reviewColumn(columns: TaskColumn[]): TaskColumn | undefined {
  return columns.find((c) => c.name.trim().toLowerCase() === 'review');
}

/**
 * Merge, Fix conflicts and (SF-T-183) Update for each of the task's open pull requests. SF-T-154: none for a sub-task's PR its
 * parent shares (`parent`): those are merged and fixed from the parent.
 */
export function planPrActions(input: {
  prs: TaskPr[];
  live: TaskPrsLiveResponse | null;
  waiting: TaskWaiting[];
  may: { merge: boolean; run: boolean };
  parent?: TaskParent | null;
}): TaskActionPlan[] {
  const open = input.prs.filter((pr) => isOpenPr(pr) && !sharedPrParent({ parent: input.parent ?? null }, pr.url));
  const several = open.length > 1;
  const plans: TaskActionPlan[] = [];
  const aiHasIt = input.waiting.some((w) => w.target === 'ai');
  for (const pr of open) {
    const row = input.live?.prs.find((p) => p.url === pr.url);
    const live = row?.live ?? null;
    const label = prLabel(pr.url);
    if (input.may.merge) {
      const state = prMergeState(live, row?.checks, row?.subtasks_not_ready, row?.migrations);
      const unread = !input.live
        ? 'Reading it on GitHub…'
        : !input.live.github_connected
          ? "GitHub isn't connected to the factory."
          : (row?.error ?? "Couldn't read it on GitHub.");
      plans.push({
        key: `merge:${pr.url}`,
        kind: 'merge',
        label: several ? `Merge ${label}` : 'Merge',
        hint: `Merge ${label} on GitHub`,
        enabled: state.enabled,
        reason: state.enabled ? null : (state.reason ?? (live ? mergeBlockedText(prMergeView(live)) : unread)),
        prUrl: pr.url,
      });
    }
    if (input.may.run && live && prMergeView(live) === 'conflicts') {
      plans.push({
        key: `fix:${pr.url}`,
        kind: 'fix_conflicts',
        label: several ? `Fix conflicts ${label}` : 'Fix conflicts',
        hint: `The AI builder merges ${live.base_ref || 'the base branch'} into ${label} and resolves the conflicts`,
        enabled: !aiHasIt,
        reason: aiHasIt ? 'The AI builder already has this task.' : null,
        prUrl: pr.url,
      });
    }
    const update = prUpdateState(row?.migrations, input.may, aiHasIt);
    if (live && update.shown) {
      plans.push({
        key: `update:${pr.url}`,
        kind: 'update',
        label: several ? `Update ${label}` : 'Update',
        hint: updateHint(label, live.base_ref),
        enabled: update.enabled,
        reason: update.reason,
        prUrl: pr.url,
      });
    }
  }
  return plans;
}

/** Send to Review and Move to Done, each left out when the task is already there. */
export function planMoveActions(input: { columnId: string; columns: TaskColumn[]; mayMove: boolean }): TaskActionPlan[] {
  if (!input.mayMove) return [];
  const plans: TaskActionPlan[] = [];
  const current = input.columns.find((c) => c.id === input.columnId);
  const review = reviewColumn(input.columns);
  if (review && review.id !== input.columnId) {
    plans.push({ key: 'review', kind: 'review', label: 'Send to Review', hint: `Move it to ${review.name}`, enabled: true, reason: null, columnId: review.id });
  }
  const done = tickColumn(input.columns, true);
  if (done && !current?.is_done) {
    plans.push({ key: 'done', kind: 'done', label: `Move to ${done.name}`, hint: `Move it to ${done.name}`, enabled: true, reason: null, columnId: done.id });
  }
  return plans;
}

/**
 * SF-T-226: the PR action that takes Run's place in the compact header (phones and the narrow panel), where
 * the PR actions otherwise wait in the More menu: one that would work now (Merge, Fix conflicts or Update).
 * With several on (more than one PR), Merge comes before Update before Fix conflicts, and then the PR linked
 * first: the actions come in the task's PR order, and a later PR usually builds on an earlier one. None when
 * no action is on. The header shows it by `headerActionLabel`, without the PR, so the close cross keeps its room.
 */
const HEADER_ORDER: TaskActionKind[] = ['merge', 'update', 'fix_conflicts'];

export function headerPrAction<T extends Pick<TaskActionPlan, 'kind' | 'enabled'>>(actions: T[]): T | null {
  for (const kind of HEADER_ORDER) {
    const first = actions.find((a) => a.enabled && a.kind === kind);
    if (first) return first;
  }
  return null;
}

/** SF-T-226: the header button's label, without the PR's number ("Fix conflicts #200" pushed the close cross off a phone). */
export const headerActionLabel = (kind: TaskActionKind): string =>
  ({ merge: 'Merge', fix_conflicts: 'Fix conflicts', update: 'Update', review: 'Send to Review', done: 'Move to Done' })[kind];
